import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Home, FileText, Calendar, DollarSign, Users, Loader2, Eye, CheckCircle, Clock, XCircle, ClipboardList } from 'lucide-react';
import config from '../config';

export const InstallerDashboard = () => {
  const { user } = useAuthStore();
  const [stats, setStats] = useState({
    total_bids: 0,
    accepted_bids: 0,
    completed_projects: 0,
    total_earnings: 0,
    pending_requests: 0
  });
  const [recentRequests, setRecentRequests] = useState([]);
  const [activeContracts, setActiveContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('access_token');

      // دریافت درخواست‌های جدید
      const requestsRes = await fetch(config.getApiUrl('/installations/requests/'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // دریافت پیشنهادات نصاب
      const bidsRes = await fetch(config.getApiUrl('/marketplace/bids/'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // دریافت قراردادهای فعال
      const contractsRes = await fetch(config.getApiUrl('/marketplace/contracts/'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (requestsRes.ok) {
        const requestsData = await requestsRes.json();
        const requests = requestsData.results || requestsData;
        setRecentRequests(requests.slice(0, 5));

        setStats(prev => ({
          ...prev,
          pending_requests: requests.filter(r => r.status === 'pending').length
        }));
      }

      if (bidsRes.ok) {
        const bidsData = await bidsRes.json();
        const bids = bidsData.results || bidsData;
        const accepted = bids.filter(b => b.status === 'accepted');
        const completed = bids.filter(b => b.installation_request?.status === 'completed');

        setStats(prev => ({
          ...prev,
          total_bids: bids.length,
          accepted_bids: accepted.length,
          completed_projects: completed.length
        }));
      }

      if (contractsRes.ok) {
        const contractsData = await contractsRes.json();
        const contracts = contractsData.results || contractsData;
        // قراردادهایی که نصاب امضا کرده و در حال اجرا هستند
        const active = contracts.filter(c =>
          c.installer_signed && (c.status === 'in_progress' || c.status === 'scheduled')
        );
        setActiveContracts(active);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'درخواست‌های جدید', value: stats.pending_requests, icon: Home, color: 'bg-blue-500' },
    { label: 'پیشنهادات ارسالی', value: stats.total_bids, icon: FileText, color: 'bg-green-500' },
    { label: 'پروژه‌های تکمیل شده', value: stats.completed_projects, icon: CheckCircle, color: 'bg-purple-500' },
    { label: 'پروژه‌های جاری', value: stats.accepted_bids, icon: Clock, color: 'bg-yellow-500' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">داشبورد نصاب</h1>
          <p className="text-gray-500">خوش آمدید {user?.username}</p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-full text-white`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link to="/installations" className="bg-blue-600 text-white rounded-xl p-6 text-center hover:bg-blue-700 transition">
            <Home className="w-8 h-8 mx-auto mb-2" />
            <h3 className="font-bold">درخواست‌های جدید</h3>
            <p className="text-sm text-blue-100 mt-1">مشاهده و ارسال پیشنهاد</p>
          </Link>

          <Link to="/installer/bids" className="bg-green-600 text-white rounded-xl p-6 text-center hover:bg-green-700 transition">
            <FileText className="w-8 h-8 mx-auto mb-2" />
            <h3 className="font-bold">پیشنهادات من</h3>
            <p className="text-sm text-green-100 mt-1">مدیریت پیشنهادات ارسالی</p>
          </Link>

          <Link to="/installer/contracts" className="bg-purple-600 text-white rounded-xl p-6 text-center hover:bg-purple-700 transition">
            <Calendar className="w-8 h-8 mx-auto mb-2" />
            <h3 className="font-bold">قراردادها</h3>
            <p className="text-sm text-purple-100 mt-1">مدیریت پروژه‌های جاری</p>
          </Link>
        </div>

// در بخش پروژه‌های فعال، کد زیر را جایگزین کنید:
        {activeContracts.map((contract) => (
          <div key={contract.id} className="p-6 hover:bg-gray-50 transition">
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div>
                <p className="font-bold text-gray-900">قرارداد #{contract.id}</p>
                <p className="text-sm text-gray-500">{contract.installation_request?.address?.substring(0, 50)}...</p>
                <p className="text-xs text-gray-400 mt-1">
                  مبلغ: {contract.bid?.proposed_cost?.toLocaleString()} تومان
                </p>
                {contract.start_date && (
                  <p className="text-xs text-gray-400">
                    شروع: {new Date(contract.start_date).toLocaleDateString('fa-IR')}
                  </p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Link
                  to={`/installer/schedule/${contract.id}`}
                  className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1"
                >
                  <Calendar className="w-4 h-4" />
                  زمان‌بندی
                </Link>
                <Link
                  to={`/project/track/${contract.id}`}
                  className="bg-green-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-green-700 flex items-center gap-1"
                >
                  <Eye className="w-4 h-4" />
                  پیگیری
                </Link>
                <Link
                  to={`/installer/progress/${contract.id}`}
                  className="bg-purple-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-purple-700 flex items-center gap-1"
                >
                  <ClipboardList className="w-4 h-4" />
                  ثبت پیشرفت
                </Link>
              </div>
            </div>
          </div>
        ))}

        {/* Recent Requests */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-bold">درخواست‌های جدید</h2>
            <Link to="/installations" className="text-blue-600 text-sm hover:underline">مشاهده همه</Link>
          </div>

          {recentRequests.length === 0 ? (
            <div className="p-12 text-center text-gray-500">هیچ درخواست جدیدی وجود ندارد</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {recentRequests.map((req) => (
                <div key={req.id} className="p-6 hover:bg-gray-50 transition">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-900">{req.address?.substring(0, 50)}...</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {req.roof_type && `نوع سقف: ${req.roof_type} | `}
                        {req.roof_area && `مساحت: ${req.roof_area} متر مربع`}
                      </p>
                    </div>
                    <Link
                      to={`/installations/${req.id}`}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1"
                    >
                      <Eye className="w-4 h-4" />
                      ارسال پیشنهاد
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
